import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import { forwardRef, useRef, useState, useCallback, useEffect, useImperativeHandle } from 'react';
import { ChevronDown, Loader, X, Search } from 'react-feather';
import include from '../../utils/include.js';
import { SearchStatus } from './declarations/Select.types.js';
import defaultStyles from './styles/Select.module.scss.js';
import { useRenderSelect } from './utils/useRenderSelect.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Enter, ArrowUp, ArrowDown } from '../../utils/keycode.js';
import { isObject } from '../../utils/is.js';
import { Button } from '../button/Button.js';

const SelectView = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    theme,
    size = 'md',
    allowClear,
    loading,
    disabled,
    suffixIcon,
    arrowIcon,
    clearIcon,
    placeholder,
    value,
    inputValue,
    popupVisible,
    maxTagCount,
    isMultiple,
    isEmptyValue,
    prefix,
    renderTag,
    onKeyDown,
    onChangeInputValue,
    onClear,
    onFocus,
    onBlur,
    onRemoveCheckedItem,
    validationStatus,
    name,
    label,
    idSuffix,
    hasId,
    optionMap,
    ...rest
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });

  // refs
  const refInput = useRef(null);
  const refWrapper = useRef(null);

  // state
  const [searchStatus, setSearchStatus] = useState(SearchStatus.NONE);
  const [focused, setFocused] = useState(false);
  const showSearch = 'showSearch' in props ? props.showSearch : isMultiple;
  const showSearchIcon = 'showSearchIcon' in props && props.showSearchIcon;
  const canFocusInput = showSearch;
  const mergedFocused = focused || popupVisible;
  const isRetainInputValueSearch = isObject(showSearch) && showSearch.retainInputValue;
  // the formatted text of value.
  const renderedValue = !isMultiple && value !== undefined ? value : '';

  // Avoid losing focus caused by clicking certain icons
  const keepFocus = event => {
    event && event.preventDefault();
  };
  const handleFocus = useCallback(action => {
    const element = canFocusInput ? refInput.current : refWrapper.current;
    if (element) {
      action === 'focus' ? element.focus() : element.blur();
    }
  }, [canFocusInput]);
  const tryTriggerFocusChange = (action, event) => {
    // The focus event at this time should be triggered by the input element
    if (canFocusInput && event.target === refWrapper.current) {
      return;
    }
    if (action === 'focus') {
      setFocused(true);
      onFocus && onFocus(event);
    } else {
      setFocused(false);
      onBlur && onBlur(event);
    }
  };
  const tryTriggerKeyDown = event => {
    // The keyboard event at this time should be triggered by the input element, ignoring the bubbling up keyboard event
    if (canFocusInput && event.currentTarget === refWrapper.current) {
      return;
    }

    // Prevent the default behavior of the browser when pressing Enter (to avoid submit event in <form>) and the arrow keys (to avoid scrolling the page)
    const keyCode = event.keyCode || event.which;
    if ([Enter.code, ArrowUp.code, ArrowDown.code].includes(keyCode)) {
      event.preventDefault();
    }
    onKeyDown && onKeyDown(event);
  };
  const mergedArrowIcon = arrowIcon ?? /*#__PURE__*/jsx("span", {
    className: styles.arrow,
    children: /*#__PURE__*/jsx(ChevronDown, {})
  });
  const isSearchIcon = showSearchIcon ? /*#__PURE__*/jsx(Search, {}) : mergedArrowIcon;
  const sufIcon = suffixIcon ? {
    suffixIcon
  } : isSearchIcon;
  const mergedSuffixIcon = loading ? /*#__PURE__*/jsx("span", {
    className: styles.loading_icon,
    children: /*#__PURE__*/jsx(Loader, {})
  }) : sufIcon;

  // event handling of input box
  const inputEventHandlers = {
    keyDown: tryTriggerKeyDown,
    focus: event => {
      event.stopPropagation();
      setSearchStatus(SearchStatus.BEFORE);
      tryTriggerFocusChange('focus', event);
    },
    blur: event => {
      event.stopPropagation();
      tryTriggerFocusChange('blur', event);
    },
    change: (newValue, event) => {
      setSearchStatus(SearchStatus.EDITING);
      onChangeInputValue && onChangeInputValue(newValue, event);
    }
  };
  const {
    renderSingle,
    renderMultiple
  } = useRenderSelect({
    searchStatus,
    inputValue,
    isRetainInputValueSearch,
    renderedValue,
    placeholder,
    canFocusInput,
    name,
    inputEventHandlers,
    refInput,
    disabled,
    styles,
    value,
    maxTagCount,
    onRemoveCheckedItem,
    showSearch,
    size,
    renderTag,
    label,
    idSuffix,
    hasId,
    optionMap
  });
  useEffect(() => {
    handleFocus(popupVisible ? 'focus' : 'blur');
    if (isRetainInputValueSearch) {
      if (!popupVisible) {
        setSearchStatus(SearchStatus.NONE);
      }
      return;
    }
    if (canFocusInput) {
      setSearchStatus(popupVisible ? SearchStatus.BEFORE : SearchStatus.NONE);
    }
  }, [canFocusInput, handleFocus, isRetainInputValueSearch, popupVisible]);
  useImperativeHandle(ref, () => ({
    refObject: refWrapper.current,
    focus: handleFocus.bind(null, 'focus'),
    blur: handleFocus.bind(null, 'blur'),
    getWidth: () => refWrapper.current && refWrapper.current.clientWidth
  }));
  const multipleSizeClassNames = clsx({
    [styles.multiple_xs]: size === 'xs',
    [styles.multiple_sm]: size === 'sm',
    [styles.multiple_md]: size === 'md',
    [styles.multiple_lg]: size === 'lg'
  });
  const sizeClassNames = clsx({
    [styles.xs]: size === 'xs',
    [styles.sm]: size === 'sm',
    [styles.md]: size === 'md',
    [styles.lg]: size === 'lg'
  });
  const validationStatusClassName = clsx({
    [styles.success]: validationStatus === 'success',
    [styles.warning]: validationStatus === 'warning',
    [styles.error]: validationStatus === 'error'
  });
  const multipleClassNames = clsx(styles.root, {
    [styles.active]: !isEmptyValue && label,
    [styles.multiple]: isMultiple,
    [styles.show_search]: showSearch,
    [styles.open]: popupVisible,
    [styles.focused]: mergedFocused,
    [styles.disabled]: disabled,
    [multipleSizeClassNames]: size && isMultiple,
    [sizeClassNames]: size && !isMultiple,
    [validationStatusClassName]: validationStatus
  });
  const mergedClearIcon = !disabled && !isEmptyValue && allowClear ? /*#__PURE__*/jsx(Button, {
    theme: {
      root: styles.clear
    },
    flat: true,
    shape: "circle",
    status: "secondary",
    size: "xs",
    onMouseDown: keepFocus,
    onKeyDown: event => {
      const keyCode = event.keyCode || event.which;
      if (keyCode === Enter.code) {
        onClear(event);
        refInput.current.focus();
      }
    },
    onClick: onClear,
    children: clearIcon || /*#__PURE__*/jsx(X, {})
  }, "clearIcon") : null;
  return /*#__PURE__*/jsxs("div", {
    ...include(rest, ['onClick', 'onMouseEnter', 'onMouseLeave']),
    ref: refWrapper,
    role: "combobox",
    "aria-controls": name,
    "aria-expanded": popupVisible,
    "aria-haspopup": "listbox",
    tabIndex: disabled ? -1 : 0,
    className: multipleClassNames
    // When there is an input box, the keyboard events are handled inside the input box to avoid triggering redundant events in the Chinese input method
    ,
    onKeyDown: tryTriggerKeyDown,
    onFocus: event => {
      if (!disabled) {
        // Focus on the input, otherwise you need to press the Tab key twice to focus on the input box
        if (canFocusInput) {
          refInput.current && refInput.current.focus();
        } else {
          tryTriggerFocusChange('focus', event);
        }
      }
    },
    onBlur: event => tryTriggerFocusChange('blur', event),
    children: [prefix && /*#__PURE__*/jsx("div", {
      className: clsx(styles.prefix),
      children: prefix
    }), isMultiple ? renderMultiple : renderSingle(), (mergedClearIcon || mergedSuffixIcon) && /*#__PURE__*/jsx("div", {
      className: styles.suffix,
      children: /*#__PURE__*/jsxs(Fragment, {
        children: [mergedClearIcon, mergedSuffixIcon]
      })
    })]
  });
});
SelectView.displayName = 'SelectView';

export { SelectView };
